/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import { Paper, Typography, useMediaQuery, Box } from '@mui/material';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import React from 'react';
import { useMatch } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { exportModeAtom } from '../map/atoms';
import { computeTrend } from './computeTrend';

type Props = {
  label: string;
  value: number | string;
  prevValue?: number | string;
};

export const StatsBox = React.memo(({ label, value, prevValue }: Props) => {
  const matches = useMediaQuery('(min-width: 1200px)');
  const width = matches ? 150 : 50;
  const isExportMode = useAtomValue(exportModeAtom);
  const match = useMatch({
    path: '/:incidentId/:sectionId/:viewType/:attributeId?',
  });

  const trend = React.useMemo(() => computeTrend(value, prevValue), [value, prevValue]);

  return (
    <Paper
      elevation={1}
      sx={{
        backgroundColor: isExportMode
          ? 'rgba(0, 62, 103, 1)'
          : match?.params.viewType === 'map'
            ? 'rgba(0, 62, 103, 0.6)'
            : 'rgba(0, 62, 103, 1)',
        backdropFilter: isExportMode ? 'none' : 'blur(7px)',
        minWidth: width,
        width: '220px',
        minHeight: '120px',
        transition: 'all 0.3s ease-in-out',
        borderRadius: 1,
        color: 'white',
        textAlign: 'center',
        marginBottom: 0.5,
        marginLeft: 0.5,
        direction: 'ltr',
        maxWidth: '100px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',

        '@media print': {
          minWidth: 50,
        },
        padding: 0.5,
      }}
    >
      <Typography
        variant={matches ? 'h5' : 'h5'}
        sx={{
          color: 'white',
          fontWeight: 'bold',
          letterSpacing: '0.05em',
        }}
      >
        {value.toLocaleString('en-US', {
          maximumSignificantDigits: 3,
          maximumFractionDigits: 2,
          compactDisplay: 'short',
          notation: 'compact',
        })}
      </Typography>
      <Typography
        variant={matches ? 'h6' : 'subtitle1'}
        sx={{
          color: 'white',
          paddingTop: 0.5,
          paddingBottom: 0.5,
          letterSpacing: '0.05em',
        }}
      >
        {label}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        {trend?.percentChange && (
          <Typography
            variant={matches ? 'body1' : 'body2'}
            sx={{
              color: trend?.direction === 'up' ? '#ff0000' : '#00ff00',
            }}
          >
            {trend?.percentChange.toLocaleString('en-US', {
              maximumSignificantDigits: 3,
              maximumFractionDigits: 2,
              compactDisplay: 'short',
              notation: 'compact',
            })}
            %
          </Typography>
        )}
        {trend?.direction === 'up' && <ArrowDropUp sx={{ margin: 0, color: '#ff0000' }} />}
        {trend?.direction === 'down' && <ArrowDropDown sx={{ color: '#00ff00' }} />}
      </Box>
    </Paper>
  );
});
